/**
 * MODULE: _shared/lib/frontend/api/dealIntelligenceApi.ts
 * PURPOSE: Sales intelligence signals + deal scores client (Sprint 11).
 * DEPENDENCIES: ./config (apiV1Url, getAuthHeaders)
 * PUBLIC_API: getDealScore, getDealSignals, SignalType, DealScore, SalesSignal
 * SIDE_EFFECTS: HTTP GET to /api/v1/sales-intelligence/scores|signals
 * AGENT_SAFETY: RESTRICTED
 * RECENT: 2026-05-26 [2.2.1225] — default to live API (false); opt-in mock via env only (fixes journey E2E route mocks).
 * RECENT_PRIOR: 2026-05-24 [2.2.1137] — shouldUseDealIntelligenceMock (UAT default); gateway 429 backoff; no fetch when rate-limited.
 */

import { apiV1Url, getAuthHeaders } from './config';
import { bumpGateway429FromResponse, isInGatewayBackoff } from './gatewayRateLimit';
import { readApiAuthReady } from './sessionAuthReady';

export type SignalType =
 | 'high_intent'
 | 're_engagement'
 | 'churn_risk'
 | 'deal_velocity'
 | 'stalled';

export type SalesSignal = {
 signalId: string;
 tenantId: string;
 dealId?: string | null;
 contactId?: string | null;
 signalType: SignalType;
 confidence: number;
 sourceEventIds: string[];
 detectedAt: string;
 metadata?: Record<string, unknown>;
};

export type ScoreFactor = {
 factor: string;
 weight: number;
 value: number;
 contribution: number;
};

export type DealScore = {
 dealId: string;
 tenantId: string;
 score: number;
 grade: 'A' | 'B' | 'C' | 'D';
 scoreFactors: ScoreFactor[];
 scoredAt: string;
 nextActionHint?: string | null;
};

export const DEAL_INTELLIGENCE_USE_MOCK =
 typeof import.meta !== 'undefined' &&
 (import.meta.env?.MODE === 'test' || import.meta.env?.VITE_DEAL_INTELLIGENCE_MOCK === 'true');

/** Skip signals/scores HTTP in UAT (mirrors workspaceApi mock default; avoids gateway 429 noise). */
function shouldUseDealIntelligenceMock(): boolean {
 if (DEAL_INTELLIGENCE_USE_MOCK) return true;
 if (typeof import.meta !== 'undefined') {
  const env = import.meta.env as { VITE_SKIP_SALES_INTELLIGENCE_API?: string; VITE_USE_MOCK_DATA?: string };
  if (env.VITE_SKIP_SALES_INTELLIGENCE_API === 'true') return true;
  if (env.VITE_USE_MOCK_DATA === 'true') return true;
 }
 try {
  const tenantId = localStorage.getItem('tenant_id') || '';
  const tenantName = localStorage.getItem('tenant_name') || '';
  if (tenantId.toLowerCase().includes('acme') || tenantName.toLowerCase().includes('acme')) {
   return true;
  }
 } catch {
  /* ignore */
 }
 if (typeof import.meta !== 'undefined') {
  const env = import.meta.env as { VITE_DEAL_INTELLIGENCE_MOCK?: string };
  if (env.VITE_DEAL_INTELLIGENCE_MOCK === 'false') return false;
 }
 return false;
}

function mapSignal(raw: Record<string, unknown>): SalesSignal {
 return {
 signalId: String(raw.signal_id ?? raw.signalId ?? ''),
 tenantId: String(raw.tenant_id ?? raw.tenantId ?? ''),
 dealId: (raw.deal_id ?? raw.dealId) as string | null | undefined,
 contactId: (raw.contact_id ?? raw.contactId) as string | null | undefined,
 signalType: String(raw.signal_type ?? raw.signalType ?? 'stalled') as SignalType,
 confidence: Number(raw.confidence ?? 0),
 sourceEventIds: Array.isArray(raw.source_event_ids)
 ? raw.source_event_ids.map(String)
 : Array.isArray(raw.sourceEventIds)
 ? raw.sourceEventIds.map(String)
 : [],
 detectedAt: String(raw.detected_at ?? raw.detectedAt ?? new Date().toISOString()),
 metadata: (raw.metadata as Record<string, unknown>) ?? {},
 };
}

function mapScore(raw: Record<string, unknown>): DealScore {
 const factorsRaw = raw.score_factors ?? raw.scoreFactors;
 const factors = Array.isArray(factorsRaw)
 ? factorsRaw.map((f) => ({
 factor: String((f as ScoreFactor).factor ?? ''),
 weight: Number((f as ScoreFactor).weight ?? 0),
 value: Number((f as ScoreFactor).value ?? 0),
 contribution: Number((f as ScoreFactor).contribution ?? 0),
 }))
 : [];
 return {
 dealId: String(raw.deal_id ?? raw.dealId ?? ''),
 tenantId: String(raw.tenant_id ?? raw.tenantId ?? ''),
 score: Number(raw.score ?? 0),
 grade: String(raw.grade ?? 'C') as DealScore['grade'],
 scoreFactors: factors,
 scoredAt: String(raw.scored_at ?? raw.scoredAt ?? new Date().toISOString()),
 nextActionHint: (raw.next_action_hint ?? raw.nextActionHint) as string | null | undefined,
 };
}

const MOCK_SCORE: DealScore = {
 dealId: 'deal-mock',
 tenantId: 'tnt_mock',
 score: 85,
 grade: 'A',
 scoreFactors: [{ factor: 'signal_high_intent', weight: 20, value: 0.9, contribution: 18 }],
 scoredAt: new Date().toISOString(),
 nextActionHint: 'Follow up within 2 days while engagement is high.',
};

const MOCK_SIGNALS: SalesSignal[] = [
 {
 signalId: 'sig-1',
 tenantId: 'tnt_mock',
 dealId: 'deal-mock',
 signalType: 'high_intent',
 confidence: 0.92,
 sourceEventIds: ['e1'],
 detectedAt: new Date().toISOString(),
 },
 {
 signalId: 'sig-2',
 tenantId: 'tnt_mock',
 dealId: 'deal-2',
 signalType: 'stalled',
 confidence: 0.71,
 sourceEventIds: ['e2'],
 detectedAt: new Date(Date.now() - 86_400_000).toISOString(),
 },
];

export async function getDealScore(dealId: string): Promise<DealScore | null> {
 if (!dealId?.trim()) return null;
 if (shouldUseDealIntelligenceMock()) {
 return { ...MOCK_SCORE, dealId };
 }
 if (isInGatewayBackoff()) return null;
 const res = await fetch(apiV1Url(`/sales-intelligence/scores/${encodeURIComponent(dealId)}`), {
 headers: getAuthHeaders(),
 });
 if (res.status === 429) {
 bumpGateway429FromResponse(res);
 return null;
 }
 if (res.status === 404) return null;
 if (!res.ok) throw new Error(await res.text());
 return mapScore((await res.json()) as Record<string, unknown>);
}

export type GetDealSignalsParams = {
 dealId: string;
 signalType?: SignalType;
 limit?: number;
};

export async function getDealSignals(params: GetDealSignalsParams): Promise<SalesSignal[]> {
 const { dealId, signalType, limit = 20 } = params;
 if (!dealId?.trim()) return [];
 return fetchSignalsFromApi({ dealId, signalType, limit });
}

export type ListTenantSignalsParams = {
 signalType?: SignalType;
 limit?: number;
};

/** Tenant-wide signal list (Activity feed Signals tab). */
export async function listTenantSignals(params: ListTenantSignalsParams = {}): Promise<SalesSignal[]> {
 const { signalType, limit = 50 } = params;
 return fetchSignalsFromApi({ signalType, limit });
}

async function fetchSignalsFromApi(options: {
 dealId?: string;
 signalType?: SignalType;
 limit: number;
}): Promise<SalesSignal[]> {
 const { dealId, signalType, limit } = options;
 if (!readApiAuthReady()) return [];
 if (shouldUseDealIntelligenceMock()) {
 return MOCK_SIGNALS.filter((s) => !dealId || s.dealId === dealId || dealId === 'deal-mock');
 }
 if (isInGatewayBackoff()) {
 return [];
 }
 const qs = new URLSearchParams({ limit: String(limit) });
 if (dealId?.trim()) qs.set('deal_id', dealId.trim());
 if (signalType) qs.set('signal_type', signalType);
 const res = await fetch(apiV1Url(`/sales-intelligence/signals?${qs.toString()}`), {
 headers: getAuthHeaders(),
 });
 if (res.status === 429) {
 bumpGateway429FromResponse(res);
 return [];
 }
 if (!res.ok) throw new Error(await res.text());
 const body = (await res.json()) as unknown;
 if (!Array.isArray(body)) return [];
 return body.map((row) => mapSignal(row as Record<string, unknown>));
}
